import axios, { AxiosInstance } from 'axios';
import { ApiResponse, GetUploadUrlsResponse, UploadUrlResponse, JobResponse } from '../types';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    // Single API Gateway for all endpoints
    this.client = axios.create({
      baseURL: 'https://200tsb7821.execute-api.us-east-2.amazonaws.com/default',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Start async export job by submitting files to processing queue.
   * Returns jobId that can be used to poll for status.
   */
  async startExport(uniqueFileNames: string[]): Promise<ApiResponse<{ jobId: string }>> {
    try {
      const payload = {
        uniqueFileNames
      };
      
      const response = await fetch(`${this.client.defaults.baseURL}/submit-job`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📤 Job submitted:', data);

      if (data.success && data.data?.jobId) {
        return {
          success: true,
          data: { jobId: data.data.jobId },
        };
      } else {
        return {
          success: false,
          error: data.error || 'Failed to start export job',
        };
      }
    } catch (error: any) {
      console.error('Error starting export job:', error);
      
      let errorMessage = 'Failed to start export job.';
      if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Get status of an export job by jobId.
   * Returns current status, progress, and downloadUrl if completed.
   */
  async getExportStatus(jobId: string): Promise<ApiResponse<JobResponse>> {
    try {
      const response = await fetch(`${this.client.defaults.baseURL}/readStatus?jobId=${encodeURIComponent(jobId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: false,
            error: `Job ${jobId} not found`,
          };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📊 Job status:', data);

      if (data.success && data.data) {
        return {
          success: true,
          data: data.data as JobResponse,
        };
      } else {
        return {
          success: false,
          error: data.error || 'Failed to get job status',
        };
      }
    } catch (error: any) {
      console.error('Error getting export status:', error);
      
      let errorMessage = 'Failed to get export status.';
      if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  async getUploadUrls(files: File[]): Promise<ApiResponse<GetUploadUrlsResponse>> {
    try {
      // For multiple files, we'll make separate GET requests
      // Use new unified API Gateway for getUploadUrl endpoint
      const uploadPromises = files.map(file => 
        this.client.get<GetUploadUrlsResponse>(`/getUploadUrl?fileName=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type)}`)
      );

      const responses = await Promise.all(uploadPromises);
      
      // Combine all responses into a single response
      const combinedFiles = responses.flatMap(response => response.data.files);

      // Log the results for verification
      console.log('📋 Upload URLs received:');
      combinedFiles.forEach((file, index) => {
        console.log(`File ${index + 1}:`);
        console.log(`  Original Name: ${file.originalFileName}`);
        console.log(`  Unique Name: ${file.uniqueFileName}`);
        console.log(`  Upload URL: ${file.uploadUrl}`);
        console.log('---');
      });

      return {
        success: true,
        data: { files: combinedFiles },
      };
    } catch (error: any) {
      console.error('Error getting upload URLs:', error);
      
      let errorMessage = 'Failed to get upload URLs.';
      
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      } else if (error.request) {
        errorMessage = 'Unable to connect to the service.';
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  async uploadFileToS3(file: File, uploadUrl: string): Promise<ApiResponse<void>> {
    try {
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error uploading file to S3:', error);
      return {
        success: false,
        error: `Failed to upload ${file.name}`,
      };
    }
  }

  async uploadFiles(files: File[]): Promise<ApiResponse<UploadUrlResponse[]>> {
    try {
      // Get upload URLs
      const urlResponse = await this.getUploadUrls(files);
      if (!urlResponse.success || !urlResponse.data) {
        return {
          success: false,
          error: urlResponse.error || 'Failed to get upload URLs',
        };
      }

      // Upload each file to S3
      const uploadResults: UploadUrlResponse[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uploadInfo = urlResponse.data.files[i];
        
        const uploadResult = await this.uploadFileToS3(file, uploadInfo.uploadUrl);
        
        if (uploadResult.success) {
          uploadResults.push(uploadInfo);
        } else {
          return {
            success: false,
            error: uploadResult.error || `Failed to upload ${file.name}`,
          };
        }
      }

      return {
        success: true,
        data: uploadResults,
      };
    } catch (error: any) {
      console.error('Error uploading files:', error);
      return {
        success: false,
        error: 'An unexpected error occurred during upload.',
      };
    }
  }

  async exportXlsx(fileNames: string[], poeApiKey?: string): Promise<ApiResponse<{ downloadUrl: string; filesProcessed: number; message: string }>> {
    try {
      const payload: { uniqueFileNames: string[]; poeApiKey?: string } = {
        uniqueFileNames: fileNames
      };
      
      // Only include poeApiKey if provided (fallback if not set as Lambda env var)
      if (poeApiKey) {
        payload.poeApiKey = poeApiKey;
      }
      
      // Use simple POST without custom headers to avoid preflight
      const response = await fetch('https://h7s42bjfq5.execute-api.us-east-2.amazonaws.com/default/exportXlsx', {
        method: 'POST',
        body: JSON.stringify(payload),
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📊 Export response received:', data);

      // Backend returns { success, data: { downloadUrl, filesProcessed, message, ... } }
      // Unwrap to match ExportXlsxResponse type expected by the app
      return {
        success: true,
        data: data?.data,
      };
    } catch (error: any) {
      console.error('Error exporting to Excel:', error);
      
      let errorMessage = 'Failed to process files and export to Excel.';
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}

const apiService = new ApiService();
export default apiService;